import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import "./Contact.css";

// ── EmailJS configuration ────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID = "service_gt9b1o1";
const EMAILJS_TEMPLATE_ID = "template_5py0nkm";
const EMAILJS_PUBLIC_KEY = "3pZ4LLXOvfpZLozIb";

const subjects = [
  "General Enquiry",
  "Volunteer",
  "Donation",
  "Partnership",
  "Media",
];

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = "Name is required.";
  if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email))
    errors.email = "A valid email address is required.";
  if (!form.subject) errors.subject = "Please select a subject.";
  if (!form.message.trim()) errors.message = "Message is required.";
  return errors;
}

export default function Contact() {
  const formRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  // FIX: map the visible input's name (c_name, c_email…) back to the
  // correct state key (name, email…) so typing actually updates the form.
  const fieldMap = {
    c_name: "name",
    c_email: "email",
    c_phone: "phone",
    c_subject: "subject",
    c_message: "message",
  };

  const handleChange = (e) => {
    const stateKey = fieldMap[e.target.name] || e.target.name;
    setForm((f) => ({ ...f, [stateKey]: e.target.value }));
    if (errors[stateKey]) setErrors((er) => ({ ...er, [stateKey]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const errs = validate(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setStatus("sending");
    setErrorMsg("");

    emailjs
      .sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, formRef.current, {
        publicKey: EMAILJS_PUBLIC_KEY,
      })
      .then(() => {
        setStatus("success");
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      })
      .catch((err) => {
        console.error("EmailJS error:", err);
        setStatus("error");
        setErrorMsg(
          "Your message could not be sent right now. Please try again or email us directly at admin@foodnutritionfoundation.com",
        );
      });
  };

  return (
    <main>
      {/* HERO */}
      <section className="page-hero" aria-label="Contact page hero">
        <div
          className="page-hero-bg"
          style={{
            backgroundImage:
              "url('images/gallery/smiling-children-02.jpg')",
          }}
        />
        <div className="container page-hero-content">
          <span className="eyebrow" style={{ color: "var(--amber-light)" }}>
            We'd Love to Hear From You
          </span>
          <h1>Contact Us</h1>
          <p>
            Reach out with questions, partnership proposals, media enquiries, or
            just to learn more about what we do.
          </p>
        </div>
      </section>

      {/* CONTACT LAYOUT */}
      <section
        className="section contact-section"
        aria-labelledby="contact-heading"
      >
        <div className="container">
          <div className="contact-layout">
            {/* SIDEBAR */}
            <aside className="contact-sidebar">
              <h2 id="contact-heading" className="contact-sidebar-title">
                Get in Touch
              </h2>
              <div className="divider" />

              <div className="contact-block">
                <div className="contact-block-icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92v2z" />
                  </svg>
                </div>
                <div>
                  <h3>Phone — South African Secretariat</h3>
                  <p>
                    <a href="tel:+27653855857">+27 65 385 5857</a>
                  </p>
                  <p className="contact-note">
                    Primary contact for all enquiries
                  </p>
                </div>
              </div>

              <div className="contact-block">
                <div className="contact-block-icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92v2z" />
                  </svg>
                </div>
                <div>
                  <h3>Phone — Zambia Operations</h3>
                  <p>
                    <a href="tel:+260978360000">+260 97 8360000</a>
                  </p>
                  <p className="contact-note">Kamanga Compound, Lusaka</p>
                </div>
              </div>

              <div className="contact-block">
                <div className="contact-block-icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div>
                  <h3>Email</h3>
                  <p>
                    <a href="mailto:admin@foodnutritionfoundation.com">
                      admin@foodnutritionfoundation.com
                    </a>
                  </p>
                </div>
              </div>

              <div className="contact-block">
                <div className="contact-block-icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <h3>Operations Address</h3>
                  <p>
                    Kamanga Compound
                    <br />
                    Lusaka, Zambia
                  </p>
                </div>
              </div>

              <div className="contact-block">
                <div className="contact-block-icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="9" cy="21" r="1" />
                    <circle cx="20" cy="21" r="1" />
                    <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 001.95-1.57L23 6H6" />
                  </svg>
                </div>
                <div>
                  <h3>Online Store</h3>
                  <p>
                    <a
                      href="https://www.bulungu.co.za"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      bulungu.co.za
                    </a>
                  </p>
                  <p className="contact-note">
                    Shop handmade crafts from our Skills Programme clients
                  </p>
                </div>
              </div>

              {/* MAP PLACEHOLDER */}
              <div
                className="map-placeholder"
                aria-label="Map showing Kamanga Compound, Lusaka, Zambia"
              >
                <div className="map-placeholder-inner">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="map-pin-icon"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <p>
                    Kamanga Compound
                    <br />
                    Lusaka, Zambia
                  </p>
                  <a
                    href="https://www.google.com/maps/search/Kamanga+Compound+Lusaka+Zambia"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-green btn-sm"
                    style={{ marginTop: "0.75rem" }}
                  >
                    Open in Google Maps
                  </a>
                </div>
              </div>
            </aside>

            {/* FORM */}
            <div className="contact-form-wrap">
              <h2 className="contact-form-title">Send Us a Message</h2>
              <div className="divider" />

              {status === "success" ? (
                <div className="form-success">
                  <div className="form-success-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h3>Message Sent!</h3>
                  <p>
                    Thank you for reaching out. We aim to respond within 2–3
                    business days.
                  </p>
                  <button
                    className="btn btn-outline-green"
                    style={{ marginTop: "1.25rem" }}
                    onClick={() => setStatus("idle")}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  noValidate
                  className="contact-form"
                >
                  {/* Hidden inputs with the exact EmailJS template variable
                      names — these are what emailjs.sendForm() reads from
                      the DOM. The visible inputs below use c_* names to
                      avoid collision, and handleChange maps them back to
                      the matching state key. */}
                  <input type="hidden" name="from_name" value={form.name} readOnly />
                  <input type="hidden" name="from_email" value={form.email} readOnly />
                  <input type="hidden" name="phone" value={form.phone} readOnly />
                  <input type="hidden" name="subject" value={form.subject} readOnly />
                  <input type="hidden" name="message" value={form.message} readOnly />

                  <div className="grid-2">
                    <div className="form-group">
                      <label htmlFor="c-name">Full Name *</label>
                      <input
                        id="c-name"
                        name="c_name"
                        type="text"
                        className={`form-control${errors.name ? " error" : ""}`}
                        value={form.name}
                        onChange={handleChange}
                        autoComplete="name"
                        placeholder="Your full name"
                        disabled={status === "sending"}
                      />
                      {errors.name && (
                        <p className="error-msg" role="alert">
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="c-email">Email Address *</label>
                      <input
                        id="c-email"
                        name="c_email"
                        type="email"
                        className={`form-control${
                          errors.email ? " error" : ""
                        }`}
                        value={form.email}
                        onChange={handleChange}
                        autoComplete="email"
                        placeholder="your@email.com"
                        disabled={status === "sending"}
                      />
                      {errors.email && (
                        <p className="error-msg" role="alert">
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="c-phone">
                      Phone Number{" "}
                      <span
                        style={{ fontWeight: 400, color: "var(--text-muted)" }}
                      >
                        (Optional)
                      </span>
                    </label>
                    <input
                      id="c-phone"
                      name="c_phone"
                      type="tel"
                      className="form-control"
                      value={form.phone}
                      onChange={handleChange}
                      autoComplete="tel"
                      placeholder="+27 000 000 0000"
                      disabled={status === "sending"}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="c-subject">Subject *</label>
                    <select
                      id="c-subject"
                      name="c_subject"
                      className={`form-control${
                        errors.subject ? " error" : ""
                      }`}
                      value={form.subject}
                      onChange={handleChange}
                      disabled={status === "sending"}
                    >
                      <option value="">Select a subject...</option>
                      {subjects.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    {errors.subject && (
                      <p className="error-msg" role="alert">
                        {errors.subject}
                      </p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="c-message">Message *</label>
                    <textarea
                      id="c-message"
                      name="c_message"
                      className={`form-control${
                        errors.message ? " error" : ""
                      }`}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us how we can help..."
                      rows={6}
                      disabled={status === "sending"}
                    />
                    {errors.message && (
                      <p className="error-msg" role="alert">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  {status === "error" && (
                    <div className="contact-send-error" role="alert">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      <span>{errorMsg}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary contact-submit-btn"
                    disabled={status === "sending"}
                  >
                    {status === "sending" ? (
                      <>
                        <span className="contact-spinner" aria-hidden="true" />
                        Sending...
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* DONATE CTA */}
      <section className="cta-band" aria-label="Donate CTA">
        <div className="container cta-band-inner">
          <div>
            <h2>Ready to Make a Difference?</h2>
            <p>
              Your donation goes directly to the people who need it most in
              Kamanga Compound.
            </p>
          </div>
          <div className="cta-band-actions">
            <Link to="/donate" className="btn btn-primary btn-lg">
              Donate Now
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
